import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, X, History, Wrench, Package, Trash2, AlertTriangle, QrCode, Camera, Plus, MapPin, Search, Loader2, Check, Globe, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import QRCodeGenerator from './QRCodeGenerator';
import PricingSection from './PricingSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country and State/Province data
const COUNTRIES = [
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'OTHER', name: 'Other', flag: '🌍' },
];

const STATES_BY_COUNTRY = {
  AU: [
    { code: 'NSW', name: 'New South Wales' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'WA', name: 'Western Australia' },
    { code: 'SA', name: 'South Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'NT', name: 'Northern Territory' },
    { code: 'ACT', name: 'Australian Capital Territory' },
  ],
  NZ: [
    { code: 'NTL', name: 'Northland' },
    { code: 'AUK', name: 'Auckland' },
    { code: 'WKO', name: 'Waikato' },
    { code: 'BOP', name: 'Bay of Plenty' },
    { code: 'GIS', name: 'Gisborne' },
    { code: 'HKB', name: "Hawke's Bay" },
    { code: 'TKI', name: 'Taranaki' },
    { code: 'MWT', name: 'Manawatū-Whanganui' },
    { code: 'WGN', name: 'Wellington' },
    { code: 'TAS', name: 'Tasman' },
    { code: 'NSN', name: 'Nelson' },
    { code: 'MBH', name: 'Marlborough' },
    { code: 'WTC', name: 'West Coast' },
    { code: 'CAN', name: 'Canterbury' },
    { code: 'OTA', name: 'Otago' },
    { code: 'STL', name: 'Southland' },
  ],
  US: [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  ],
  GB: [
    { code: 'ENG', name: 'England' }, { code: 'SCT', name: 'Scotland' },
    { code: 'WLS', name: 'Wales' }, { code: 'NIR', name: 'Northern Ireland' },
  ],
  CA: [
    { code: 'AB', name: 'Alberta' }, { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' }, { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' }, { code: 'NS', name: 'Nova Scotia' },
    { code: 'ON', name: 'Ontario' }, { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' }, { code: 'SK', name: 'Saskatchewan' },
    { code: 'NT', name: 'Northwest Territories' }, { code: 'NU', name: 'Nunavut' }, { code: 'YT', name: 'Yukon' },
  ],
  ZA: [
    { code: 'EC', name: 'Eastern Cape' }, { code: 'FS', name: 'Free State' },
    { code: 'GP', name: 'Gauteng' }, { code: 'KZN', name: 'KwaZulu-Natal' },
    { code: 'LP', name: 'Limpopo' }, { code: 'MP', name: 'Mpumalanga' },
    { code: 'NC', name: 'Northern Cape' }, { code: 'NW', name: 'North West' },
    { code: 'WC', name: 'Western Cape' },
  ],
};

const EditYardDialog = ({ open, onClose, yard, onUpdate, allYards, hivesInYard, beekeepers = [] }) => {
  const [yardPricing, setYardPricing] = useState({});
  const [defaultPrices, setDefaultPrices] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    street_address: '',
    suburb: '',
    state: '',
    state_name: '',
    postcode: '',
    country: 'AU',
    country_name: 'Australia',
    gps_lat: 0,
    gps_lng: 0,
    notes: '',
    image_url: '',
    assigned_beekeeper_id: '',
    tools: {},
    miscellaneous: {
      diatomaceous_earth: false,
      rake: false,
      spade: false,
      other: '',
      other_list: [],
      diatomaceous_earth_price: 0,
      rake_price: 0,
      spade_price: 0,
      other_price: 0
    },
    yard_pricing: {},
    owner_name: '',
    owner_surname: '',
    owner_mobile: ''
  });
  const [uploading, setUploading] = useState(false);
  const [changeHistory, setChangeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [moveToYard, setMoveToYard] = useState('');
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [adminTools, setAdminTools] = useState([]);
  const [adminComponents, setAdminComponents] = useState([]);
  const [suburbSuggestions, setSuburbSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locationVerified, setLocationVerified] = useState(false);
  const [suggestionName, setSuggestionName] = useState('');
  const [seasonalSettings, setSeasonalSettings] = useState({
    hemisphere: '',
    spring: { interval_days: 10, enabled: true },
    summer: { interval_days: 14, enabled: true },
    autumn: { interval_days: 21, enabled: true },
    winter: { interval_days: 0, enabled: false },
  });

  // Handle tool suggestion
  const handleSuggestTool = async () => {
    if (!suggestionName.trim()) return;
    try {
      const res = await fetch(`${API}/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: suggestionName.trim(),
          type: 'tool',
          suggested_section: '',
          notes: ''
        })
      });
      if (res.ok) {
        toast.success('Suggestion submitted! Your admin will review it.');
        setSuggestionName('');
      } else {
        toast.error('Failed to submit suggestion');
      }
    } catch (error) {
      toast.error('Failed to submit suggestion');
    }
  };

  // Get states for selected country
  const availableStates = STATES_BY_COUNTRY[formData.country] || [];

  // Fetch admin-configured tools and components (active items only)
  useEffect(() => {
    const fetchAdminConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const [toolsRes, componentsRes, pricesRes] = await Promise.all([
          fetch(`${API}/config/admin_tools`, { headers }),
          fetch(`${API}/config/admin_components`, { headers }),
          fetch(`${API}/hive-costs`, { headers })
        ]);
        if (toolsRes.ok) {
          const toolsData = await toolsRes.json();
          setAdminTools(Array.isArray(toolsData) ? toolsData : (toolsData.items || []));
        }
        if (componentsRes.ok) {
          const componentsData = await componentsRes.json();
          setAdminComponents(Array.isArray(componentsData) ? componentsData : (componentsData.items || []));
        }
        if (pricesRes.ok) {
          const pricesData = await pricesRes.json();
          setDefaultPrices(pricesData.prices || {});
        }
      } catch (error) {
      }
    };
    if (open) {
      fetchAdminConfig();
    }
  }, [open]);

  useEffect(() => {
    if (yard) {
      // Try to detect country from existing location
      let detectedCountry = yard.country || 'AU';
      let detectedState = yard.state || '';
      let detectedStateName = yard.state_name || '';
      
      // If location exists but country doesn't, try to parse it
      if (yard.location && !yard.country) {
        const locationParts = yard.location.split(',').map(s => s.trim());
        const lastPart = locationParts[locationParts.length - 1]?.toLowerCase();
        if (lastPart === 'australia') detectedCountry = 'AU';
        else if (lastPart === 'new zealand') detectedCountry = 'NZ';
        else if (lastPart === 'united states' || lastPart === 'usa') detectedCountry = 'US';
        else if (lastPart === 'united kingdom' || lastPart === 'uk') detectedCountry = 'GB';
        else if (lastPart === 'canada') detectedCountry = 'CA';
      }
      
      setFormData({
        name: yard.name || '',
        location: yard.location || '',
        street_address: yard.street_address || yard.address || '',
        suburb: yard.suburb || '',
        state: yard.state || detectedState,
        state_name: yard.state_name || detectedStateName,
        postcode: yard.postcode || '',
        country: yard.country || detectedCountry,
        country_name: yard.country_name || COUNTRIES.find(c => c.code === detectedCountry)?.name || 'Australia',
        gps_lat: yard.gps_lat || yard.latitude || 0,
        gps_lng: yard.gps_lng || yard.longitude || 0,
        notes: yard.notes || '',
        image_url: yard.image_url || '',
        tools: { 
          bee_brushes: 0, hive_tools: 0, smokers: 0, other: '', other_list: [],
          ...yard.tools 
        },
        miscellaneous: { 
          diatomaceous_earth: false, rake: false, spade: false, other: '', other_list: [],
          ...yard.miscellaneous 
        },
        yard_pricing: yard.yard_pricing || {},
        owner_name: yard.owner_name || '',
        owner_surname: yard.owner_surname || '',
        owner_mobile: yard.owner_mobile || '',
        assigned_beekeeper_id: yard.assigned_beekeeper_id || ''
      });
      setYardPricing({ ...defaultPrices, ...(yard.yard_pricing || {}) });
      setLocationVerified(!!(yard.gps_lat && yard.gps_lng));
      fetchChangeHistory();
      // Load seasonal inspection settings
      if (yard.id) {
        const token = localStorage.getItem('token');
        axios.get(`${API}/yards/${yard.id}/seasonal-inspection`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).then(res => {
          setSeasonalSettings(res.data);
        }).catch(() => { /* use defaults */ });
      }
    }
  }, [yard]);

  // Search for suburb/city within the selected state/country
  const searchSuburb = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuburbSuggestions([]);
      return;
    }
    
    setSearching(true);
    try {
      const countryName = COUNTRIES.find(c => c.code === formData.country)?.name || '';
      const stateName = formData.state_name || '';
      const searchQuery = `${query}, ${stateName}, ${countryName}`.replace(/, ,/g, ',');
      
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(searchQuery)}&count=8&language=en&format=json`
      );
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const filtered = data.results.filter(r => 
          !formData.country || formData.country === 'OTHER' || 
          r.country_code === formData.country
        );
        
        setSuburbSuggestions(filtered.map(r => ({
          name: r.name,
          admin1: r.admin1,
          admin2: r.admin2,
          country: r.country,
          country_code: r.country_code,
          latitude: r.latitude,
          longitude: r.longitude,
          postcode: r.postcodes?.[0] || '',
        })));
      } else {
        setSuburbSuggestions([]);
      }
    } catch (err) {
    } finally {
      setSearching(false);
    }
  }, [formData.country, formData.state_name]);

  // Select a suburb from suggestions
  const selectSuburb = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      suburb: suggestion.name,
      postcode: suggestion.postcode || prev.postcode,
      gps_lat: suggestion.latitude,
      gps_lng: suggestion.longitude,
    }));
    setSuburbSuggestions([]);
    setLocationVerified(true);
    toast.success(`Location set: ${suggestion.name} (${suggestion.latitude.toFixed(4)}, ${suggestion.longitude.toFixed(4)})`);
  };

  // Handle country change
  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    setFormData(prev => ({
      ...prev,
      country: countryCode,
      country_name: country?.name || '',
      state: '',
      state_name: '',
      suburb: '',
      postcode: '',
      gps_lat: 0,
      gps_lng: 0,
    }));
    setLocationVerified(false);
  };

  // Handle state change
  const handleStateChange = (stateCode) => {
    const state = availableStates.find(s => s.code === stateCode);
    setFormData(prev => ({
      ...prev,
      state: stateCode,
      state_name: state?.name || stateCode,
      suburb: '',
      postcode: '',
    }));
    setLocationVerified(false);
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    
    setSearching(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          gps_lat: latitude,
          gps_lng: longitude
        }));
        
        try {
          const tzResponse = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m&timezone=auto`
          );
          const tzData = await tzResponse.json();
          
          if (tzData.timezone) {
            const cityFromTz = tzData.timezone.split('/').pop().replace(/_/g, ' ');
            setFormData(prev => ({
              ...prev,
              suburb: cityFromTz,
            }));
          }
          
          setLocationVerified(true);
          toast.success('GPS location captured');
        } catch (err) {
          toast.success('GPS coordinates captured');
        }
        setSearching(false);
      },
      (err) => {
        toast.error('Could not get location');
        setSearching(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Helper to render custom tools for a specific section
  // Build dynamic tool sections from admin_tools
  const renderDynamicToolSections = () => {
    const sections = {};
    const sectionLabels = {
      protective_gear: { label: 'Protective Gear', icon: '🦺', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
      hive_tools: { label: 'Hive Tools', icon: '🔧', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
      feeding_equipment: { label: 'Feeding Equipment', icon: '🍯', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
      harvesting_equipment: { label: 'Harvesting Equipment', icon: '🧴', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
      heavy_equipment: { label: 'Heavy Equipment', icon: '📦', bg: 'bg-stone-100', border: 'border-stone-300', text: 'text-stone-800' },
      miscellaneous: { label: 'Miscellaneous', icon: '📋', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800' },
    };

    adminTools.filter(t => t.is_active !== false).forEach(tool => {
      const section = tool.section || 'miscellaneous';
      if (!sections[section]) sections[section] = [];
      sections[section].push(tool);
    });

    return Object.entries(sections).map(([sectionKey, sectionTools]) => {
      const style = sectionLabels[sectionKey] || {
        label: sectionKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        icon: '✨', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800'
      };
      return (
        <div key={sectionKey} className={`${style.bg} border ${style.border} rounded-xl p-3 mb-3`}>
          <h4 className={`text-sm font-semibold ${style.text} mb-2`}>{style.icon} {style.label}</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {sectionTools.map(tool => {
              const toolKey = tool.form_key || tool.name.toLowerCase().replace(/\s+/g, '_');
              return (
                <div key={tool.id}>
                  <Label className="text-xs text-stone-600"># of {tool.name}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.tools[toolKey] || 0}
                    onChange={(e) => updateTools(toolKey, parseInt(e.target.value) || 0)}
                    className="h-10 bg-white border-stone-200 rounded-lg mt-1"
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  const handleBeekeeperSelect = (value) => {
    if (value === 'none') {
      setFormData({ ...formData, assigned_beekeeper_id: '', owner_name: '', owner_surname: '', owner_mobile: '' });
    } else {
      const beekeeper = beekeepers.find(b => b.id === value);
      if (beekeeper) {
        // Parse name (can be "name" field or "first_name last_name")
        const fullName = beekeeper.name || `${beekeeper.first_name || ''} ${beekeeper.last_name || ''}`.trim();
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        setFormData({
          ...formData,
          assigned_beekeeper_id: value,
          owner_name: firstName,
          owner_surname: lastName,
          owner_mobile: beekeeper.phone || beekeeper.mobile_phone || ''
        });
      }
    }
  };

  const fetchChangeHistory = async () => {
    if (!yard?.id) return;
    try {
      const res = await axios.get(`${API}/change-history/yard/${yard.id}`);
      setChangeHistory(res.data);
    } catch (error) {
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await axios.post(`${API}/upload`, formDataUpload);
      setFormData({ ...formData, image_url: `${API}/uploads/${res.data.file_id}` });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Build full location string from structured fields
    const locationParts = [
      formData.street_address,
      formData.suburb,
      formData.state,
      formData.postcode,
      formData.country_name
    ].filter(Boolean);
    
    const fullLocation = locationParts.join(', ');
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/yards/${yard.id}`, {
        ...formData,
        location: fullLocation || formData.location,
        address: formData.street_address,
        latitude: formData.gps_lat ? parseFloat(formData.gps_lat) : null,
        longitude: formData.gps_lng ? parseFloat(formData.gps_lng) : null,
        yard_pricing: yardPricing
      }, { headers });
      // Save seasonal inspection settings
      await axios.put(`${API}/yards/${yard.id}/seasonal-inspection`, seasonalSettings, { headers }).catch(() => {});
      toast.success('Yard updated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to update yard');
    }
  };

  const handleDeleteYard = async () => {
    try {
      // Find the destination yard ID if moving hives
      let moveToYardId = null;
      if (hivesInYard && hivesInYard.length > 0 && moveToYard) {
        const destYard = allYards.find(y => y.name === moveToYard);
        moveToYardId = destYard?.id;
      }
      
      // Delete the apiary with optional move destination
      // Backend will handle moving hives and merging tools
      const deleteUrl = moveToYardId 
        ? `${API}/yards/${yard.id}?move_to_yard_id=${moveToYardId}`
        : `${API}/yards/${yard.id}`;
      
      await axios.delete(deleteUrl);
      
      if (moveToYardId && hivesInYard?.length > 0) {
        toast.success(`Yard deleted. ${hivesInYard.length} hive(s) and tools moved to ${moveToYard}`);
      } else {
        toast.success('Yard deleted successfully');
      }
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to delete yard');
    }
  };

  const updateTools = (field, value) => {
    setFormData({
      ...formData,
      tools: { ...formData.tools, [field]: value }
    });
  };

  const updateMisc = (field, value) => {
    setFormData({
      ...formData,
      miscellaneous: { ...formData.miscellaneous, [field]: value }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="edit-yard-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-stone-800 font-heading">
              Edit Apiary: {yard?.name}
            </DialogTitle>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowQRDialog(true)}
              className="h-10 px-3"
              title="Generate QR Code"
            >
              <QrCode className="h-5 w-5 mr-2" />
              QR Code
            </Button>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-1">
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Yard Image */}
          <div>
            <Label className="text-stone-700 font-medium flex items-center gap-2">
              <Upload className="h-4 w-4" /> Yard Image
            </Label>
            <div className="mt-2 flex items-center gap-4">
              {formData.image_url ? (
                <div className="relative">
                  <img src={formData.image_url} alt="Yard" className="w-32 h-32 object-cover rounded-xl border-2 border-stone-200" />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 bg-stone-100 rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center">
                  <span className="text-stone-400 text-sm">No image</span>
                </div>
              )}
              <div className="flex gap-2 flex-col">
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" capture="environment" onChange={handleImageUpload} className="hidden" id="yard-camera-capture" />
                    <div className="flex items-center gap-1 bg-purple-100 hover:bg-purple-200 text-purple-800 px-4 py-2 rounded-full font-medium transition-colors">
                      <Camera className="h-4 w-4" />
                      Camera
                    </div>
                  </label>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    <div className="flex items-center gap-1 bg-amber-100 hover:bg-amber-200 text-amber-800 px-4 py-2 rounded-full font-medium transition-colors">
                      <Upload className="h-4 w-4" />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div>
            <Label htmlFor="yard_name" className="text-stone-700 font-medium">Apiary Name *</Label>
            <Input
              id="yard_name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="h-12 bg-stone-50 border-stone-200 rounded-xl mt-1"
            />
          </div>

          {/* Location Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm text-stone-700 font-semibold flex items-center gap-2">
                <Globe className="h-4 w-4 text-blue-600" />
                Location Details
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={getCurrentLocation}
                disabled={searching}
                className="text-xs h-8"
              >
                {searching ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <MapPin className="h-3 w-3 mr-1" />}
                Use GPS
              </Button>
            </div>

            {/* Country & State Row */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs text-stone-600">Country *</Label>
                <Select value={formData.country} onValueChange={handleCountryChange}>
                  <SelectTrigger className="h-11 bg-white border-stone-200 rounded-lg mt-1">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="flex items-center gap-2">
                          <span>{c.flag}</span>
                          <span>{c.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-stone-600">State/Province *</Label>
                {availableStates.length > 0 ? (
                  <Select value={formData.state} onValueChange={handleStateChange}>
                    <SelectTrigger className="h-11 bg-white border-stone-200 rounded-lg mt-1">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableStates.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value, state_name: e.target.value })}
                    placeholder="Enter state/province"
                    className="h-11 bg-white border-stone-200 rounded-lg mt-1"
                  />
                )}
              </div>
            </div>

            {/* Suburb Search */}
            <div className="mb-3">
              <Label className="text-xs text-stone-600">Suburb/City *</Label>
              <div className="relative mt-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    placeholder={formData.state ? `Search suburbs in ${formData.state_name || formData.state}...` : "Select state first"}
                    value={formData.suburb}
                    onChange={(e) => {
                      setFormData({ ...formData, suburb: e.target.value });
                      setLocationVerified(false);
                      searchSuburb(e.target.value);
                    }}
                    disabled={!formData.state && formData.country !== 'OTHER'}
                    className="h-11 pl-10 pr-10 bg-white border-stone-200 rounded-lg"
                  />
                  {locationVerified && <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />}
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />}
                </div>

                {/* Suburb Suggestions */}
                {suburbSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {suburbSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectSuburb(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-start gap-3 border-b border-stone-100 last:border-0"
                      >
                        <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-stone-800">{suggestion.name}</div>
                          <div className="text-sm text-stone-500">
                            {suggestion.admin1 && `${suggestion.admin1}, `}{suggestion.country}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Postcode & Street */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs text-stone-600">Postcode</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="3000"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-stone-600">Street Address (optional)</Label>
                <Input
                  value={formData.street_address}
                  onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                  placeholder="123 Bee Street"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
            </div>

            {/* GPS Coordinates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600">Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.gps_lat || ''}
                  onChange={(e) => setFormData({ ...formData, gps_lat: parseFloat(e.target.value) || 0 })}
                  placeholder="-33.8688"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.gps_lng || ''}
                  onChange={(e) => setFormData({ ...formData, gps_lng: parseFloat(e.target.value) || 0 })}
                  placeholder="151.2093"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
            </div>

            {formData.gps_lat && formData.gps_lng && formData.gps_lat !== 0 && formData.gps_lng !== 0 && (
              <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                <Check className="h-3 w-3" />
                GPS: {parseFloat(formData.gps_lat).toFixed(4)}, {parseFloat(formData.gps_lng).toFixed(4)}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="yard_notes" className="text-stone-700 font-medium">Notes</Label>
            <Textarea
              id="yard_notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="bg-stone-50 border-stone-200 rounded-xl mt-1 min-h-20"
            />
          </div>

          {/* Beekeeper Assignment (Optional) */}
          {beekeepers.length > 0 && (
            <div>
              <Label className="text-stone-700 font-medium">Assign Beekeeper (Optional)</Label>
              <Select value={formData.assigned_beekeeper_id || 'none'} onValueChange={handleBeekeeperSelect}>
                <SelectTrigger className="h-12 bg-stone-50 border-stone-200 rounded-xl mt-1">
                  <SelectValue placeholder="Select a beekeeper..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No beekeeper assigned --</SelectItem>
                  {beekeepers.map(bk => (
                    <SelectItem key={bk.id} value={bk.id}>
                      {bk.name || `${bk.first_name || ''} ${bk.last_name || ''}`.trim() || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Owner Contact Details */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Label className="text-stone-700 font-semibold mb-3 block">📞 Owner Contact Details</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600">First Name</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  placeholder="John"
                  className="h-12 bg-white border-stone-200 rounded-xl mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Surname</Label>
                <Input
                  value={formData.owner_surname}
                  onChange={(e) => setFormData({ ...formData, owner_surname: e.target.value })}
                  placeholder="Smith"
                  className="h-12 bg-white border-stone-200 rounded-xl mt-1"
                />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-stone-600">Mobile Number</Label>
              <Input
                type="tel"
                value={formData.owner_mobile}
                onChange={(e) => setFormData({ ...formData, owner_mobile: e.target.value })}
                placeholder="+61 400 000 000"
                className="h-12 bg-white border-stone-200 rounded-xl mt-1"
              />
            </div>
          </div>

          <Separator />

          {/* Tools Section - Reorganized with Categories */}
          <div>
            <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2 mb-4">
              <Wrench className="h-5 w-5 text-amber-600" /> Tools & Equipment
            </h3>
            
            {renderDynamicToolSections()}
          </div>

          {/* Suggest New Tool */}
          {/* Suggest New Tool */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-amber-800 mb-2">💡 Can&apos;t find a tool you need?</h4>
            <p className="text-xs text-amber-700 mb-2">Suggest a new tool and your admin will review it.</p>
            <div className="flex gap-2">
              <Input
                placeholder="Tool name..."
                value={suggestionName}
                onChange={(e) => setSuggestionName(e.target.value)}
                className="flex-1 h-9 bg-white"
              />
              <Button 
                type="button"
                onClick={handleSuggestTool}
                disabled={!suggestionName.trim()}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 h-9"
              >
                Suggest
              </Button>
            </div>
          </div>

          {/* Yard Pricing Section */}
          <PricingSection
            pricing={yardPricing}
            onPricingChange={setYardPricing}
            tools={formData.tools}
            miscellaneous={formData.miscellaneous}
            isYardPricing={true}
            adminTools={adminTools}
            adminComponents={adminComponents}
            defaultPrices={defaultPrices}
            entityName={formData.name || 'Apiary'}
          />

          <Separator />

          {/* Seasonal Inspection Schedule */}
          <div className="space-y-4" data-testid="seasonal-inspection-section">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-600" />
              Inspection Schedule
            </h3>
            <p className="text-sm text-stone-500">Set how often hives in this apiary should be inspected per season. Hives will not show as overdue during paused seasons.</p>

            {/* Hemisphere */}
            <div className="flex items-center gap-3">
              <Globe className="h-4 w-4 text-stone-400" />
              <span className="text-sm font-medium text-stone-700 w-24">Hemisphere</span>
              <div className="flex gap-2">
                {['southern', 'northern'].map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setSeasonalSettings(prev => ({ ...prev, hemisphere: h }))}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      seasonalSettings.hemisphere === h
                        ? 'bg-amber-500 text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                    data-testid={`hemisphere-${h}`}
                  >
                    {h === 'southern' ? 'Southern' : 'Northern'}
                  </button>
                ))}
              </div>
              {seasonalSettings.auto_detected && (
                <span className="text-xs text-stone-400 italic">auto-detected from GPS</span>
              )}
            </div>

            {/* Season grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'spring', label: 'Spring', icon: '🌸', months: seasonalSettings.hemisphere === 'southern' ? 'Sep – Nov' : 'Mar – May', color: 'bg-green-50 border-green-200' },
                { key: 'summer', label: 'Summer', icon: '☀️', months: seasonalSettings.hemisphere === 'southern' ? 'Dec – Feb' : 'Jun – Aug', color: 'bg-amber-50 border-amber-200' },
                { key: 'autumn', label: 'Autumn', icon: '🍂', months: seasonalSettings.hemisphere === 'southern' ? 'Mar – May' : 'Sep – Nov', color: 'bg-orange-50 border-orange-200' },
                { key: 'winter', label: 'Winter', icon: '❄️', months: seasonalSettings.hemisphere === 'southern' ? 'Jun – Aug' : 'Dec – Feb', color: 'bg-blue-50 border-blue-200' },
              ].map(season => {
                const cfg = seasonalSettings[season.key] || { interval_days: 21, enabled: true };
                return (
                  <div key={season.key} className={`${season.color} border rounded-xl p-4 transition-opacity ${!cfg.enabled ? 'opacity-60' : ''}`} data-testid={`season-${season.key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{season.icon}</span>
                        <div>
                          <div className="font-semibold text-stone-800 text-sm">{season.label}</div>
                          <div className="text-xs text-stone-500">{season.months}</div>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-stone-500">{cfg.enabled ? 'Active' : 'Paused'}</span>
                        <div
                          onClick={() => setSeasonalSettings(prev => ({
                            ...prev,
                            [season.key]: { ...cfg, enabled: !cfg.enabled }
                          }))}
                          className={`w-10 h-5 rounded-full transition-colors cursor-pointer flex items-center ${cfg.enabled ? 'bg-green-500 justify-end' : 'bg-stone-300 justify-start'}`}
                        >
                          <div className="w-4 h-4 bg-white rounded-full shadow mx-0.5" />
                        </div>
                      </label>
                    </div>
                    {cfg.enabled && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-stone-600">Inspect every</span>
                        <input
                          type="number"
                          min="1"
                          max="120"
                          value={cfg.interval_days || ''}
                          onChange={(e) => setSeasonalSettings(prev => ({
                            ...prev,
                            [season.key]: { ...cfg, interval_days: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-16 h-8 text-center rounded-lg border border-stone-300 text-sm font-bold"
                          data-testid={`interval-${season.key}`}
                        />
                        <span className="text-xs text-stone-600">days</span>
                      </div>
                    )}
                    {!cfg.enabled && (
                      <p className="text-xs text-stone-500 mt-1 italic">Inspections paused — no overdue alerts</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Change History */}
          <div>
            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-stone-600 hover:text-stone-800 font-medium"
            >
              <History className="h-4 w-4" />
              {showHistory ? 'Hide' : 'Show'} Change History ({changeHistory.length})
            </button>
            {showHistory && (
              <div className="mt-3 bg-stone-50 rounded-xl p-4 max-h-64 overflow-y-auto">
                {changeHistory.length === 0 ? (
                  <p className="text-stone-500 text-sm">No changes recorded yet.</p>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-stone-300"></div>
                    
                    {changeHistory.map((change, idx) => (
                      <div key={idx} className="relative pl-8 pb-4 last:pb-0">
                        {/* Timeline dot */}
                        <div className="absolute left-1.5 top-1 w-3 h-3 bg-amber-500 rounded-full border-2 border-white"></div>
                        
                        <div className="bg-white border border-stone-200 rounded-lg p-3 shadow-sm">
                          <div className="flex justify-between items-start">
                            <div className="text-sm font-semibold text-stone-800 capitalize">
                              {change.field_changed.replace(/_/g, ' ')}
                            </div>
                            <div className="text-xs text-stone-400">
                              {new Date(change.changed_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-xs text-stone-500 mt-1">
                            {new Date(change.changed_at).toLocaleTimeString()} by {change.changed_by}
                          </div>
                          {change.old_value && change.new_value && (
                            <div className="mt-2 text-xs space-y-1">
                              <div className="text-red-600 line-through">
                                Old: {typeof change.old_value === 'object' ? JSON.stringify(change.old_value) : String(change.old_value)}
                              </div>
                              <div className="text-green-600">
                                New: {typeof change.new_value === 'object' ? JSON.stringify(change.new_value) : String(change.new_value)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-bold text-red-900">Delete this apiary?</h4>
                  {hivesInYard && hivesInYard.length > 0 ? (
                    <div className="mt-2">
                      <p className="text-sm text-red-800">
                        This yard has <strong>{hivesInYard.length} hive(s)</strong>. Please select where to move them:
                      </p>
                      <Select value={moveToYard} onValueChange={setMoveToYard}>
                        <SelectTrigger className="mt-2 h-12">
                          <SelectValue placeholder="Select destination yard" />
                        </SelectTrigger>
                        <SelectContent>
                          {allYards?.filter(y => y.id !== yard?.id).map(y => (
                            <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <p className="text-sm text-red-800 mt-1">This yard has no hives.</p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 h-10"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleDeleteYard}
                      disabled={hivesInYard?.length > 0 && !moveToYard}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white h-10"
                    >
                      Delete Apiary
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="h-14 px-4"
            >
              <Trash2 className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1 h-14 rounded-full font-semibold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-14 rounded-full font-bold"
            >
              Save Changes
            </Button>
          </div>
        </form>
        </div>

        <QRCodeGenerator
          open={showQRDialog}
          onClose={() => setShowQRDialog(false)}
          type="yard"
          id={yard?.id}
          name={yard?.name}
          yardLocation={yard?.location}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditYardDialog;
